// Capturing amCharts charts for report snapshots.
//
// amCharts5 renders to stacked <canvas> layers. Serialising those via
// html-to-image's `toSvg` (foreignObject) produces a blank/white image because
// canvas bitmaps aren't reliably rasterised inside a foreignObject. Instead we
// composite the canvas layers straight into a single PNG, which is deterministic
// and needs no async image loading.

/**
 * Composite all <canvas> layers inside a chart element into one PNG data URL.
 * Returns null when there is nothing to capture or the canvas is tainted.
 */
export const compositeChartCanvasesToPng = (chartElement: HTMLElement): string | null => {
  const canvases = Array.from(chartElement.querySelectorAll('canvas')) as HTMLCanvasElement[]
  if (canvases.length === 0) return null

  const base = canvases[0]
  const width = base.width || Math.round(chartElement.clientWidth)
  const height = base.height || Math.round(chartElement.clientHeight)
  if (!width || !height) return null

  const out = document.createElement('canvas')
  out.width = width
  out.height = height

  const ctx = out.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, out.width, out.height)

  for (const canvas of canvases) {
    if (!canvas.width || !canvas.height) continue
    try {
      ctx.drawImage(canvas, 0, 0, out.width, out.height)
    }
    catch {
      // A tainted canvas can't be composited — bail so the caller can fall back.
      return null
    }
  }

  try {
    return out.toDataURL('image/png')
  }
  catch {
    return null
  }
}

export interface ChartCaptureResult {
  /** Composited PNG per element id (keyed by the closest [data-element-id]). */
  chartImages: Record<string, string>
  /** Removes the temporary overlays and restores mutated styles. */
  cleanup: () => void
}

/**
 * Composite every amCharts chart under `root` to a PNG and overlay that PNG
 * on top of the live chart. This lets a subsequent full-slide `toSvg` capture
 * show the chart (the overlay is a plain <img>, which serialises correctly)
 * instead of a blank canvas. Call `cleanup()` once the slide capture is done.
 */
export const overlayChartsForCapture = async (root: HTMLElement): Promise<ChartCaptureResult> => {
  const chartImages: Record<string, string> = {}
  const removers: Array<() => void> = []

  const chartDivs = Array.from(
    root.querySelectorAll('.chart-render-target, .diagnostic-chart-render-target')
  ) as HTMLElement[]
  for (const chartDiv of chartDivs) {
    const png = compositeChartCanvasesToPng(chartDiv)
    if (!png) continue

    const holder = chartDiv.closest('[data-element-id]') as HTMLElement | null
    const id = holder?.getAttribute('data-element-id')
    if (id) chartImages[id] = png

    const img = new Image()
    img.src = png
    img.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;background:#ffffff;z-index:5;'
    try {
      await img.decode()
    }
    catch {
      // Ignore decode failures; the data URL still renders when serialised.
    }

    const prevPosition = chartDiv.style.position
    if (getComputedStyle(chartDiv).position === 'static') {
      chartDiv.style.position = 'relative'
    }
    chartDiv.appendChild(img)
    removers.push(() => {
      img.remove()
      chartDiv.style.position = prevPosition
    })
  }

  return { chartImages, cleanup: () => removers.forEach((fn) => fn()) }
}
